import React from 'react';
import { clsx } from 'clsx';

// Navigation item
export interface NavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  badge?: React.ReactNode;
  disabled?: boolean;
  children?: NavItem[];
}

// Sidebar nav item component
export interface SidebarNavItemProps {
  item: NavItem;
  isActive?: boolean;
  isCollapsed?: boolean;
  level?: number;
  onNavigate?: (item: NavItem) => void;
  className?: string;
}

export function SidebarNavItem({
  item,
  isActive = false,
  isCollapsed = false,
  level = 0,
  onNavigate,
  className,
}: SidebarNavItemProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = (e: React.MouseEvent) => {
    if (item.disabled) return;

    if (hasChildren && !isCollapsed) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    } else if (item.onClick) {
      item.onClick();
    } else if (onNavigate) {
      onNavigate(item);
    }
  };

  const content = (
    <>
      {item.icon && (
        <span className={clsx('flex-shrink-0', isCollapsed ? 'w-6 h-6' : 'w-5 h-5')}>
          {item.icon}
        </span>
      )}
      {!isCollapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && <span className="flex-shrink-0">{item.badge}</span>}
          {hasChildren && (
            <svg
              className={clsx(
                'w-4 h-4 text-gray-400 transition-transform dark:text-gray-500',
                isExpanded && 'rotate-90'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </>
      )}
    </>
  );

  const baseClasses = clsx(
    'flex items-center gap-3 w-full rounded-lg transition-colors',
    isCollapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2',
    level > 0 && !isCollapsed && 'ml-6',
    isActive
      ? 'bg-blue-50 text-blue-700'
      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200',
    item.disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  const element = item.href ? (
    <a href={item.href} className={baseClasses} onClick={handleClick}>
      {content}
    </a>
  ) : (
    <button type="button" className={baseClasses} onClick={handleClick} disabled={item.disabled}>
      {content}
    </button>
  );

  return (
    <div>
      {element}
      {hasChildren && isExpanded && !isCollapsed && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <SidebarNavItem
              key={child.id}
              item={child}
              level={level + 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Sidebar section
export interface SidebarSectionProps {
  title?: string;
  children: React.ReactNode;
  isCollapsed?: boolean;
  className?: string;
}

export function SidebarSection({
  title,
  children,
  isCollapsed = false,
  className,
}: SidebarSectionProps) {
  return (
    <div className={clsx('py-2', className)}>
      {title && !isCollapsed && (
        <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider dark:text-gray-500">
          {title}
        </h3>
      )}
      <nav className="space-y-1">{children}</nav>
    </div>
  );
}

// Sidebar header
export interface SidebarHeaderProps {
  logo?: React.ReactNode;
  title?: string;
  isCollapsed?: boolean;
  className?: string;
}

export function SidebarHeader({
  logo,
  title,
  isCollapsed = false,
  className,
}: SidebarHeaderProps) {
  return (
    <div
      className={clsx(
        'flex items-center h-16 border-b border-gray-200 dark:border-gray-700',
        isCollapsed ? 'justify-center px-2' : 'px-4 gap-3',
        className
      )}
    >
      {logo && <div className="flex-shrink-0">{logo}</div>}
      {title && !isCollapsed && (
        <h1 className="text-lg font-semibold text-gray-900 truncate dark:text-gray-100">{title}</h1>
      )}
    </div>
  );
}

// Sidebar footer
export interface SidebarFooterProps {
  children: React.ReactNode;
  isCollapsed?: boolean;
  className?: string;
}

export function SidebarFooter({
  children,
  isCollapsed = false,
  className,
}: SidebarFooterProps) {
  return (
    <div
      className={clsx(
        'border-t border-gray-200 dark:border-gray-700',
        isCollapsed ? 'p-2' : 'p-4',
        className
      )}
    >
      {children}
    </div>
  );
}

// Sidebar toggle button
export interface SidebarToggleProps {
  isCollapsed: boolean;
  onToggle: () => void;
  position?: 'inside' | 'outside';
  className?: string;
}

export function SidebarToggle({
  isCollapsed,
  onToggle,
  position = 'inside',
  className,
}: SidebarToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={clsx(
        'p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors dark:text-gray-500',
        position === 'outside' && 'absolute -right-3 top-6 bg-white border border-gray-200 shadow-sm dark:bg-gray-800 dark:border-gray-700',
        className
      )}
      aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    >
      <svg
        className={clsx(
          'w-4 h-4 transition-transform',
          isCollapsed ? 'rotate-180' : ''
        )}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
      </svg>
    </button>
  );
}

// User profile in sidebar
export interface SidebarUserProps {
  name: string;
  email?: string;
  avatar?: React.ReactNode;
  isCollapsed?: boolean;
  onClick?: () => void;
  className?: string;
}

export function SidebarUser({
  name,
  email,
  avatar,
  isCollapsed = false,
  onClick,
  className,
}: SidebarUserProps) {
  const content = (
    <>
      <div className="flex-shrink-0">
        {avatar || (
          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>
      {!isCollapsed && (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">{name}</p>
          {email && (
            <p className="text-xs text-gray-500 truncate dark:text-gray-400">{email}</p>
          )}
        </div>
      )}
    </>
  );

  const classes = clsx(
    'flex items-center gap-3 w-full rounded-lg transition-colors',
    isCollapsed ? 'justify-center p-2' : 'p-2',
    onClick && 'hover:bg-gray-100 cursor-pointer',
    className
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}

// Collapsible sidebar container
export interface CollapsibleSidebarProps {
  children: React.ReactNode;
  isCollapsed?: boolean;
  onToggle?: () => void;
  expandedWidth?: number;
  collapsedWidth?: number;
  showToggle?: boolean;
  togglePosition?: 'inside' | 'outside';
  className?: string;
}

export function CollapsibleSidebar({
  children,
  isCollapsed = false,
  onToggle,
  expandedWidth = 256,
  collapsedWidth = 64,
  showToggle = true,
  togglePosition = 'inside',
  className,
}: CollapsibleSidebarProps) {
  return (
    <aside
      className={clsx(
        'relative flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-200 dark:bg-gray-800 dark:border-gray-700',
        className
      )}
      style={{ width: isCollapsed ? collapsedWidth : expandedWidth }}
    >
      {showToggle && onToggle && togglePosition === 'inside' && (
        <div className={clsx('absolute top-4', isCollapsed ? 'right-2' : 'right-4')}>
          <SidebarToggle isCollapsed={isCollapsed} onToggle={onToggle} />
        </div>
      )}
      {showToggle && onToggle && togglePosition === 'outside' && (
        <SidebarToggle isCollapsed={isCollapsed} onToggle={onToggle} position="outside" />
      )}
      {children}
    </aside>
  );
}

// Sidebar context for passing collapsed state
export interface SidebarContextValue {
  isCollapsed: boolean;
  toggle: () => void;
}

export const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export interface SidebarProviderProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function SidebarProvider({
  children,
  defaultCollapsed = false,
  onCollapsedChange,
}: SidebarProviderProps) {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  const toggle = React.useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      onCollapsedChange?.(next);
      return next;
    });
  }, [onCollapsedChange]);

  const value = React.useMemo(() => ({ isCollapsed, toggle }), [isCollapsed, toggle]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

// Sidebar divider
export interface SidebarDividerProps {
  className?: string;
}

export function SidebarDivider({ className }: SidebarDividerProps) {
  return <hr className={clsx('my-2 border-gray-200 dark:border-gray-700', className)} />;
}

// Sidebar tooltip (for collapsed state)
export interface SidebarTooltipProps {
  label: string;
  children: React.ReactNode;
  isCollapsed?: boolean;
  className?: string;
}

export function SidebarTooltip({
  label,
  children,
  isCollapsed = false,
  className,
}: SidebarTooltipProps) {
  const [isVisible, setIsVisible] = React.useState(false);

  if (!isCollapsed) {
    return <>{children}</>;
  }

  return (
    <div
      className={clsx('relative', className)}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 px-2 py-1 text-sm text-white bg-gray-900 rounded shadow-lg whitespace-nowrap"
          role="tooltip"
        >
          {label}
        </div>
      )}
    </div>
  );
}

// Sidebar badge
export interface SidebarBadgeProps {
  count: number;
  max?: number;
  variant?: 'default' | 'primary' | 'danger';
  className?: string;
}

export function SidebarBadge({
  count,
  max = 99,
  variant = 'default',
  className,
}: SidebarBadgeProps) {
  if (count <= 0) return null;

  const displayCount = count > max ? `${max}+` : String(count);

  const variantClasses = {
    default: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    primary: 'bg-blue-100 text-blue-700',
    danger: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium rounded-full',
        variantClasses[variant],
        className
      )}
    >
      {displayCount}
    </span>
  );
}

// Mini sidebar nav (icon-only navigation)
export interface MiniSidebarProps {
  items: NavItem[];
  activeId?: string;
  onNavigate?: (item: NavItem) => void;
  className?: string;
}

export function MiniSidebar({
  items,
  activeId,
  onNavigate,
  className,
}: MiniSidebarProps) {
  return (
    <nav className={clsx('flex flex-col items-center py-4 space-y-2', className)}>
      {items.map((item) => (
        <SidebarTooltip key={item.id} label={item.label} isCollapsed>
          <button
            type="button"
            onClick={() => {
              item.onClick?.();
              onNavigate?.(item);
            }}
            disabled={item.disabled}
            className={clsx(
              'w-10 h-10 flex items-center justify-center rounded-lg transition-colors',
              activeId === item.id
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400',
              item.disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={item.label}
          >
            {item.icon}
          </button>
        </SidebarTooltip>
      ))}
    </nav>
  );
}
